/**
 * Text that may not fit, and a way to read all of it when it does not.
 *
 * The rule across the app: names, branches, paths, ids and other one-line
 * identifiers stay on one line (`lines={1}`, an ellipsis); descriptions and
 * sentences wrap, and only where a box has a fixed height are they clamped
 * (`lines={2}` or `3`). Either way, when — and only when — the text is
 * actually cut off, hovering it shows the whole text in a tooltip, and so
 * does keyboard focus: on the text itself, or, when it sits inside a button,
 * link or row, on that control. A tooltip that repeats words already on
 * screen is noise, so text that fits renders as a plain element.
 *
 * Cut is measured, not guessed: `scrollWidth > clientWidth` (or the heights,
 * for a clamp) on mount, on resize (ResizeObserver) and when the text
 * changes (MutationObserver). Screen readers always get the full words —
 * CSS truncation leaves them in the DOM, and a caller that shortens text
 * itself passes the full form as `text`, which is read out instead.
 *
 * Inside SVG or a canvas overlay, where a React Aria tooltip cannot anchor,
 * use a `title` (and `<title>` in SVG) instead. test/fitText.test.ts holds
 * the decision; test/fitTextGuard.test.ts keeps new cut-off text from
 * landing without this or a `title`.
 */
import { Tooltip } from '@heroui/react';
import { mergeRefs } from '@react-aria/utils';
import {
  createElement,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { CONTROL_SELECTOR, FIT_CLASS, type FitLines, fitMode, fullText, isCut, isShortened } from './fitTextModel.ts';

/** The workbench's hint delay (ui/Hint.tsx): long enough not to flash, short enough to feel like an answer. */
const HINT_DELAY_MS = 250;

type FitTag = 'span' | 'div' | 'p' | 'blockquote' | 'code' | 'strong' | 'em' | 'small' | 'h2' | 'h3' | 'h4' | 'h5' | 'dt' | 'dd' | 'li' | 'label';

export interface FitTextProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** 1 (the default) keeps the text on one line with an ellipsis; 2–6 clamps a wrapping text to that many lines. */
  lines?: FitLines;
  /** The element to render; a `span` unless the layout needs a block. */
  as?: FitTag;
  /**
   * The full words, when what shows is a shortened form of them (a sliced
   * summary, a short id) or the children are not plain text. The tooltip and
   * screen readers get this; the tooltip shows even when nothing is cut,
   * since the visible words are not all of it.
   */
  text?: string;
  /** Where the tooltip opens; `top` unless the text sits at the top of a pane. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children?: ReactNode;
}

/** The classes a focusable cut-off text needs to show where focus is. */
const FOCUSABLE_CLASS = 'cursor-default rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus';

export function FitText({ lines = 1, as = 'span', className, text, placement = 'top', children, ...rest }: FitTextProps) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [cut, setCut] = useState(false);
  const [rendered, setRendered] = useState<string | undefined>(undefined);
  const [control, setControl] = useState<HTMLElement | null>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [controlFocused, setControlFocused] = useState(false);

  const childText = typeof children === 'string' || typeof children === 'number' ? String(children) : undefined;
  const shortened = isShortened(text, childText ?? rendered);
  const mode = fitMode({ cut, shortened, insideControl: control !== null });
  const full = fullText(text, childText, rendered);

  const attach = useCallback((el: HTMLElement | null) => setNode(el), []);

  useLayoutEffect(() => {
    if (!node) return;
    const measure = () => {
      setCut(isCut(node, lines));
      setRendered(node.textContent ?? undefined);
      setControl(node.parentElement?.closest<HTMLElement>(CONTROL_SELECTOR) ?? null);
    };
    measure();
    const resize = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    resize?.observe(node);
    const words = typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(measure);
    words?.observe(node, { characterData: true, childList: true, subtree: true });
    return () => {
      resize?.disconnect();
      words?.disconnect();
    };
  }, [node, lines]);

  // Inside a button or row: keyboard focus on that control opens the tooltip.
  useLayoutEffect(() => {
    if (!control || mode !== 'tooltip-in-control') return;
    const onIn = () => setControlFocused(control.matches(':focus-visible'));
    const onOut = () => setControlFocused(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setControlFocused(false);
    };
    if (control.contains(document.activeElement)) onIn();
    control.addEventListener('focusin', onIn);
    control.addEventListener('focusout', onOut);
    control.addEventListener('keydown', onKey);
    return () => {
      control.removeEventListener('focusin', onIn);
      control.removeEventListener('focusout', onOut);
      control.removeEventListener('keydown', onKey);
      setControlFocused(false);
    };
  }, [control, mode]);

  const classes = [FIT_CLASS[lines], className].filter(Boolean).join(' ');
  // The visible words; when they are a shortened form, screen readers get `text` from the sibling below.
  const shown = shortened ? <span aria-hidden="true">{children}</span> : children;
  const spoken = shortened ? <span className="sr-only">{text}</span> : null;

  // A stable merged ref per Tooltip trigger ref, so React does not detach and re-attach every render.
  const merged = useRef<{ from: unknown; ref: Ref<HTMLElement> } | null>(null);
  const mergedRef = (from: Ref<HTMLElement> | undefined): Ref<HTMLElement> => {
    if (!merged.current || merged.current.from !== from) merged.current = { from, ref: mergeRefs(from, attach) };
    return merged.current.ref;
  };

  if (mode === 'plain') {
    return (
      <>
        {createElement(as, { ...rest, ref: attach, className: classes }, shown)}
        {spoken}
      </>
    );
  }

  const ownStop = mode === 'tooltip';
  return (
    <>
      <Tooltip delay={HINT_DELAY_MS} isOpen={hoverOpen || controlFocused} onOpenChange={setHoverOpen}>
        <Tooltip.Trigger
          render={(props) => {
            // Take React Aria's hover / focus wiring, but not its button role or inline-block class:
            // this is text, and its own classes decide how it lays out.
            const { ref, className: _ariaClass, role: _role, tabIndex: _tab, ...aria } = props as HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> };
            return createElement(
              as,
              {
                ...rest,
                ...aria,
                ref: mergedRef(ref),
                className: ownStop ? `${classes} ${FOCUSABLE_CLASS}` : classes,
                tabIndex: ownStop ? 0 : undefined,
                // Says, in the DOM, that this text is cut and carries its tooltip (for a quick audit in devtools).
                'data-fit-cut': '',
              },
              shown,
            );
          }}
        />
        <Tooltip.Content placement={placement} className="max-w-sm whitespace-pre-line [overflow-wrap:anywhere] [word-break:normal]">
          {full}
        </Tooltip.Content>
      </Tooltip>
      {spoken}
    </>
  );
}
